import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ExpiredDemoInfo {
  is_expired_demo: boolean;
  demo_company_id?: string;
  expires_at?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  companies: any[];
  activeCompany: any | null;
  globalRoles: string[];
  portalOneCompanyId: string | null;
  effectiveCompanyId: string | null;
  isGlobalAdmin: boolean;
  loading: boolean;
  dataLoaded: boolean;
  expiredDemoInfo: ExpiredDemoInfo | null;
  checkingDemo: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  setActiveCompany: (company: any) => void;
  getRedirectPath: () => string;
  hasGlobalRole: (role: string) => boolean;
  hasCompanyRole: (companyId: string, role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<any | null>(null);
  const [globalRoles, setGlobalRoles] = useState<string[]>([]);
  const [portalOneCompanyId, setPortalOneCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [expiredDemoInfo, setExpiredDemoInfo] = useState<ExpiredDemoInfo | null>(null);
  const [checkingDemo, setCheckingDemo] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Invalidate entry target cache on auth change
        queryClient.invalidateQueries({ queryKey: ['entry-target'] });
        
        if (session?.user) {
          // Defer profile, companies and demo check to avoid race conditions
          setTimeout(() => {
            Promise.all([
              fetchProfile(session.user.id),
              fetchUserCompanies(session.user.id),
              checkExpiredDemo(session.user.id)
            ]).catch(error => {
              console.error('Error loading user data:', error);
            }).finally(() => {
              console.log('User data loaded after auth change');
              setDataLoaded(true);
              setLoading(false);
            });
          }, 0);
        } else {
          setProfile(null);
          setCompanies([]);
          setActiveCompanyState(null);
          setGlobalRoles([]);
          setExpiredDemoInfo(null);
          setDataLoaded(false);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          Promise.all([
            fetchProfile(session.user.id),
            fetchUserCompanies(session.user.id),
            checkExpiredDemo(session.user.id)
          ]).catch(error => {
            console.error('Error loading initial user data:', error);
          }).finally(() => {
            console.log('Initial user data loaded');
            setDataLoaded(true);
            setLoading(false);
          });
        }, 0);
      } else {
        setDataLoaded(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Buscar Portal One Company ID
  useEffect(() => {
    const fetchPortalOneId = async () => {
      try {
        const { data, error } = await supabase.rpc('get_portal_one_company_id');
        
        if (!error && data) {
          setPortalOneCompanyId(data);
          console.log('✅ Portal One Company ID loaded:', data);
        }
      } catch (error) {
        console.error('❌ Error fetching Portal One ID:', error);
      }
    };

    fetchPortalOneId();
  }, []);

  const fetchProfile = async (userId: string): Promise<void> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      
      setProfile(data);

      // Check if user needs to change password (but avoid redirect loop)
      if (data?.force_password_change && data?.password_reset_token) {
        const currentPath = window.location.pathname;
        console.log('🔄 Force password change detected, current path:', currentPath);
        
        // Only redirect if not already on reset password page
        if (!currentPath.includes('/reset-password')) {
          console.log('🚀 Redirecting to reset password page');
          window.location.href = `/reset-password?token=${data.password_reset_token}`;
          return;
        } else {
          console.log('✅ Already on reset password page, skipping redirect');
        }
      }

      // Fetch global roles
      const { data: globalRolesData, error: globalRolesError } = await supabase
        .from('user_global_roles')
        .select('role')
        .eq('user_id', userId);

      if (!globalRolesError && globalRolesData) {
        setGlobalRoles(globalRolesData.map(r => r.role));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const checkExpiredDemo = async (userId: string): Promise<void> => {
    try {
      setCheckingDemo(true);
      console.log('🔍 Checking demo expiry for user:', userId);
      
      const { data, error } = await supabase
        .from('demo_users_expiry')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'expired')
        .maybeSingle();

      console.log('📊 Demo check result:', { 
        user_id: userId,
        data, 
        error, 
        is_expired: !!data 
      });

      if (error) {
        console.error('❌ Error checking demo:', error);
        setExpiredDemoInfo({ is_expired_demo: false });
        return;
      }

      if (!data) {
        console.log('✅ User is not an expired demo');
        setExpiredDemoInfo({ is_expired_demo: false });
        return;
      }

      console.log('🚨 EXPIRED DEMO DETECTED:', {
        demo_company_id: data.company_id,
        expires_at: data.expires_at
      });

      setExpiredDemoInfo({
        is_expired_demo: true,
        demo_company_id: data.company_id,
        expires_at: data.expires_at
      });
    } catch (error) {
      console.error('Error checking demo expiry:', error);
      setExpiredDemoInfo({ is_expired_demo: false });
    } finally {
      setCheckingDemo(false);
    }
  };

  const fetchUserCompanies = async (userId: string): Promise<void> => {
    try {
      const { data, error } = await supabase
        .from('user_companies')
        .select(`
          *,
          companies:company_id (
            id,
            name,
            slug,
            doc_cnpj,
            status,
            timezone,
            endereco_logradouro,
            endereco_numero,
            endereco_complemento,
            endereco_bairro,
            endereco_cidade,
            endereco_estado,
            endereco_cep,
            telefone,
            email,
            branding
          )
        `)
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error fetching companies:', error);
        return;
      }
      
      const userCompanies = data?.map(uc => ({
        ...uc.companies,
        role: uc.role
      })) || [];
      
      setCompanies(userCompanies);
      
      // Restore active company from localStorage if available, otherwise pick the first
      const storedCompanyId = localStorage.getItem('activeCompanyId');
      console.log('📦 [use-auth] Restoring active company:', {
        storedCompanyId,
        userCompaniesCount: userCompanies.length,
        companies: userCompanies.map(c => ({ id: c.id, name: c.name, role: c.role }))
      });

      if (userCompanies.length > 0) {
        const preferred = userCompanies.find(c => c.id === storedCompanyId) || userCompanies[0];
        console.log('✅ [use-auth] Setting active company:', {
          name: preferred.name,
          id: preferred.id,
          role: preferred.role
        });
        setActiveCompanyState(preferred);
        
        // Garantir que localStorage sempre tenha o ID correto
        if (!storedCompanyId || storedCompanyId !== preferred.id) {
          localStorage.setItem('activeCompanyId', preferred.id);
          console.log('💾 [use-auth] Updated localStorage with active company ID:', preferred.id);
        }
      } else {
        console.warn('⚠️ [use-auth] No companies found for user');
        setActiveCompanyState(null);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        toast({
          variant: "destructive",
          title: "Erro no login",
          description: error.message === "Invalid login credentials" 
            ? "Email ou senha incorretos" 
            : error.message,
        });
      }
      
      return { error };
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro no login",
        description: "Ocorreu um erro inesperado",
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      console.log('SignUp attempt:', { email, fullName, redirectUrl });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          }
        }
      });
      
      console.log('SignUp response:', { data, error });
      
      if (error) {
        console.error('SignUp error:', error);
        toast({
          variant: "destructive",
          title: "Erro no cadastro",
          description: error.message === "User already registered" 
            ? "Este email já está cadastrado" 
            : error.message,
        });
      } else {
        console.log('SignUp successful, user created:', data.user?.id);
        toast({
          title: "Cadastro realizado!",
          description: "Verifique seu email para confirmar a conta",
        });
      }
      
      return { error };
    } catch (error: any) {
      console.error('Unexpected signup error:', error);
      toast({
        variant: "destructive",
        title: "Erro no cadastro",
        description: "Ocorreu um erro inesperado",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
      setCompanies([]);
      setActiveCompanyState(null);
      setDataLoaded(false);
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso",
      });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const setActiveCompany = (company: any) => {
    setActiveCompanyState(company);
    localStorage.setItem('activeCompanyId', company?.id || '');
  };

  const getRedirectPath = () => {
    console.log('getRedirectPath called with:', { 
      globalRoles, 
      companies: companies.length, 
      activeCompany: activeCompany?.slug,
      dataLoaded,
      user: user?.id 
    });

    // Wait for data to be loaded before determining redirect
    if (!dataLoaded) {
      console.log('Data not loaded yet, waiting...');
      return '/'; // Return root path instead of null
    }

    if (!user) {
      return '/login';
    }

    // Prefer global admin areas when user has any global role
    if (globalRoles.includes('portal_admin') || globalRoles.includes('license_manager') || globalRoles.includes('auditor')) {
      console.log('Redirecting to admin area for global role');
      return '/admin';
    }

    // Company admin: go to the admin area of the active company (or first admin company)
    const isAdminRole = (role?: string) => role === 'company_admin' || role === 'admin_company';
    const adminCompany = (activeCompany && isAdminRole(activeCompany.role))
      ? activeCompany
      : companies.find(c => isAdminRole(c.role));
    if (adminCompany) {
      console.log('Redirecting to company admin area:', adminCompany.slug);
      return `/company/${adminCompany.slug}/admin`;
    }

    // For regular users, use entry target to determine correct system redirect
    const targetCompany = activeCompany || companies[0];
    if (targetCompany) {
      console.log('Redirecting to company user area via entry target:', targetCompany.slug, 'role:', targetCompany.role);
      // Return a default path, the entry target logic will handle the actual redirect
      return `/company/${targetCompany.slug}/systems/fleet`;
    }

    // If user exists but no companies, redirect to company selection
    if (companies.length === 0) {
      console.log('User exists but no companies, redirecting to selection');
      return '/company/select';
    }

    // Fallback to home page
    console.log('Fallback to home');
    return '/';
  };

  const hasGlobalRole = (role: string) => {
    return globalRoles.includes(role);
  };

  const hasCompanyRole = (companyId: string, role: string) => {
    const company = companies.find(c => c.id === companyId);
    return company?.role === role;
  };

  const isGlobalAdmin = globalRoles.includes('portal_admin');
  
  // Para portal admins: usar Portal One Company
  // Para company admins: usar activeCompany ou primeira empresa como fallback
  const effectiveCompanyId = isGlobalAdmin 
    ? portalOneCompanyId 
    : activeCompany?.id || (companies.length > 0 ? companies[0].id : null);

  console.log('🎯 [use-auth] Effective Company ID calculated:', {
    isGlobalAdmin,
    portalOneCompanyId,
    activeCompanyId: activeCompany?.id,
    fallbackUsed: !activeCompany?.id && companies.length > 0,
    finalCompanyId: effectiveCompanyId,
    companiesAvailable: companies.length
  });

  const value = {
    user,
    session,
    profile,
    companies,
    activeCompany,
    globalRoles,
    portalOneCompanyId,
    effectiveCompanyId,
    isGlobalAdmin,
    loading,
    dataLoaded,
    expiredDemoInfo,
    checkingDemo,
    signIn,
    signUp,
    signOut,
    setActiveCompany,
    getRedirectPath,
    hasGlobalRole,
    hasCompanyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}